import * as THREE from 'three';

/**
 * Graphics presets, and the one place that applies them.
 *
 * ## Why every pass is built regardless of preset
 *
 * `PostFX` can skip constructing GTAO and SMAA (`quality: 'low'`), which saves
 * their render targets. It also makes the choice permanent: a player who picks
 * Performance and finds it ugly would need a reload to get the passes back.
 * Presets here only flip `.enabled`, the render scale and the shadow map, so
 * switching is instant and you can see the difference while standing still.
 * The targets that go unused cost a few megabytes of VRAM, which is the right
 * trade against a reload.
 *
 * ## The order the levers were chosen in
 *
 * Render scale first, because its cost is quadratic and it is the only lever
 * that helps a GPU-bound machine no matter what the bottleneck is. Then GTAO
 * (a full screen-space AO pass plus a denoise), then shadow filtering, then
 * bloom and antialiasing. Grade is never dropped: it is one cheap pass and it
 * carries the damage vignette and the flashbang wash, which are gameplay.
 */
export const QUALITY_PRESETS = [
  {
    id: 'ultra',
    name: 'ULTRA',
    sub: 'Everything, and more of it',
    blurb: 'Full-resolution rendering, ambient occlusion at 24 samples and a '
      + '4K shadow map. For a machine with frames to spare.',
    scale: 1.0,
    gtao: true,
    gtaoSamples: 24,
    bloom: true,
    smaa: true,
    shadows: 4096,
    shadowType: THREE.PCFSoftShadowMap,
  },
  {
    id: 'high',
    name: 'HIGH',
    sub: 'The way it is meant to look',
    blurb: 'The default the game is graded and lit for: ambient occlusion, '
      + 'bloom, soft shadows and antialiasing, at full resolution.',
    scale: 1.0,
    gtao: true,
    gtaoSamples: 16,
    bloom: true,
    smaa: true,
    shadows: 3072,
    shadowType: THREE.PCFSoftShadowMap,
  },
  {
    id: 'balanced',
    name: 'BALANCED',
    sub: 'Drops the ambient occlusion',
    blurb: 'Keeps bloom, antialiasing and shadows; drops the screen-space '
      + 'ambient occlusion and renders at 85%. The biggest saving for the '
      + 'least visible change.',
    scale: 0.85,
    gtao: false,
    bloom: true,
    smaa: true,
    shadows: 2048,
    shadowType: THREE.PCFShadowMap,
  },
  {
    id: 'performance',
    name: 'PERFORMANCE',
    sub: 'Post-processing off',
    blurb: 'No ambient occlusion, no bloom, no antialiasing, hard shadows at '
      + '70% resolution. Edges get rougher; the game reads the same.',
    scale: 0.7,
    gtao: false,
    bloom: false,
    smaa: false,
    shadows: 1024,
    shadowType: THREE.PCFShadowMap,
  },
  {
    id: 'potato',
    name: 'POTATO',
    sub: 'No shadows at all',
    blurb: 'Everything off and half resolution. For an integrated GPU or a '
      + 'laptop that is throttling — it will look flat, and it will run.',
    scale: 0.55,
    gtao: false,
    bloom: false,
    smaa: false,
    shadows: 0,
    shadowType: THREE.BasicShadowMap,
  },
];

export const DEFAULT_QUALITY = 'high';
const STORE_KEY = 'blackout.quality';

export function qualityById(id) {
  return QUALITY_PRESETS.find((q) => q.id === id) ?? QUALITY_PRESETS.find((q) => q.id === DEFAULT_QUALITY);
}

/** The player's saved choice, or the default. */
export function savedQuality() {
  try { return qualityById(localStorage.getItem(STORE_KEY) ?? DEFAULT_QUALITY).id; }
  catch { return DEFAULT_QUALITY; }
}

export function saveQuality(id) {
  try { localStorage.setItem(STORE_KEY, id); } catch { /* private mode */ }
}

/**
 * Apply a preset to a live game.
 *
 * The single writer of every value here. Spreading these across the menu, the
 * engine and PostFX is how you end up with a setting that half-applies.
 *
 * @param {{engine, postfx, atmosphere}} game
 * @param {string} id
 */
export function applyQuality(game, id) {
  const q = qualityById(id);
  const { engine, postfx, atmosphere } = game;

  engine.setRenderScale(q.scale);

  if (postfx) {
    if (postfx.gtao) {
      postfx.gtao.enabled = q.gtao;
      // The aerial-perspective pass reads GTAO's depth and normal buffers, so
      // it lives and dies with it. PostFX.render already ties them together;
      // this keeps them in step for the frame before the next render.
      if (postfx.aerial) postfx.aerial.enabled = q.gtao;
      if (q.gtao && q.gtaoSamples) {
        postfx.gtao.updateGtaoMaterial({ samples: q.gtaoSamples });
      }
    }
    if (postfx.bloom) postfx.bloom.enabled = q.bloom;
    if (postfx.smaa) postfx.smaa.enabled = q.smaa;
    // Lens dirt samples the bloom mip. With bloom disabled that buffer holds
    // whatever was in it last, so the dirt has to go with it or the screen
    // keeps a smear of the last bright thing that happened.
    if (postfx.grade) postfx.grade.uniforms.uDirt.value = q.bloom ? 0.5 : 0.0;
  }

  applyShadows(engine, atmosphere, q);
  game.quality = q.id;
  return q;
}

/**
 * Shadows, which are the awkward one.
 *
 * Turning the shadow map off or changing its filter changes the SHADER every
 * lit material compiles, so three.js has to be told to rebuild them. Resizing
 * the map only needs the old render target thrown away — three.js allocates the
 * new one on the next frame from `mapSize`.
 */
function applyShadows(engine, atmosphere, q) {
  const sun = atmosphere?.sun;
  const renderer = engine.renderer;
  const wantEnabled = q.shadows > 0;
  const recompile = renderer.shadowMap.enabled !== wantEnabled
    || renderer.shadowMap.type !== q.shadowType;

  renderer.shadowMap.enabled = wantEnabled;
  renderer.shadowMap.type = q.shadowType;

  if (sun) {
    sun.castShadow = wantEnabled;
    if (wantEnabled && sun.shadow.mapSize.x !== q.shadows) {
      sun.shadow.mapSize.set(q.shadows, q.shadows);
      // Dropping the target is what makes the new size take effect; without
      // this the map keeps its original resolution for the life of the page.
      sun.shadow.map?.dispose();
      sun.shadow.map = null;
    }
  }

  if (recompile) {
    renderer.shadowMap.needsUpdate = true;
    for (const scene of [engine.scene, engine.viewmodelScene]) {
      scene.traverse((o) => {
        if (!o.material) return;
        for (const m of Array.isArray(o.material) ? o.material : [o.material]) m.needsUpdate = true;
      });
    }
  }
}
